/**
 * AuditLog.tsx — Sprint 24.4
 *
 * Reads GET /admin/audit-log?since=&action=&actor=
 *
 * TODO(Sprint 24.1 backend): The /admin/audit-log HTTP endpoint must be added
 * to the admin HTTP layer. Until it lands, the page shows a stub banner and
 * returns gracefully on 404/501.
 *
 * Expected response shape:
 *   { entries: AuditLogEntry[], total?: number }
 *
 * where AuditLogEntry = {
 *   log_id?, timestamp, actor_account_id?, actor_email?,
 *   action, target_type?, target_id?, changes_json?
 * }
 */

import { useState, useEffect, useCallback } from 'react';
import { listAuditLog, AuditLogEntry, AdminApiError } from '../api/admin-client';

const LIMIT = 50;

// Actions that exist in the system — used for the filter dropdown.
// Extend as the audit log grows.
const KNOWN_ACTIONS = [
  '',
  'account.ban',
  'account.suspend',
  'account.unsuspend',
  'account.warn',
  'report.resolve',
  'shard.restart',
  'admin.role_grant',
  'stripe.dead_letter.retry',
  'stripe.dead_letter.dismiss',
];

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function actionBadge(action: string) {
  if (action.includes('ban'))      return <span className="badge badge-red">{action}</span>;
  if (action.includes('suspend'))  return <span className="badge badge-amber">{action}</span>;
  if (action.includes('warn'))     return <span className="badge badge-amber">{action}</span>;
  if (action.includes('dismiss'))  return <span className="badge badge-gray">{action}</span>;
  if (action.includes('grant'))    return <span className="badge badge-blue">{action}</span>;
  return <span className="badge badge-blue">{action}</span>;
}

type ExpandedRow = { entry: AuditLogEntry; open: boolean };

export default function AuditLog() {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [endpointMissing, setEndpointMissing] = useState(false);

  // Filters
  const [filterAction, setFilterAction] = useState('');
  const [filterActor, setFilterActor]   = useState('');
  const [filterSince, setFilterSince]   = useState('');

  // Pagination
  const [offset, setOffset] = useState(0);

  // Expanded rows (changes_json)
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await listAuditLog({
        action: filterAction || undefined,
        actor:  filterActor  || undefined,
        since:  filterSince  || undefined,
        limit:  LIMIT,
        offset,
      });
      setEntries(result.entries ?? []);
      setTotal(result.total ?? result.entries?.length ?? 0);
      setEndpointMissing(false);
    } catch (err: any) {
      if (err?.status === 404 || err?.status === 501 || err?.code === 'not_found') {
        setEndpointMissing(true);
        setError('');
      } else {
        setError(err?.message ?? 'Failed to load audit log');
      }
    } finally {
      setLoading(false);
    }
  }, [filterAction, filterActor, filterSince, offset]);

  useEffect(() => { load(); }, [load]);

  function applyFilters(e: React.FormEvent) {
    e.preventDefault();
    setOffset(0);
    load();
  }

  function toggleExpand(key: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const pageCount = Math.max(1, Math.ceil(total / LIMIT));
  const currentPage = Math.floor(offset / LIMIT) + 1;

  return (
    <div>
      <div className="page-title">Audit Log</div>

      {/* Stub banner — shown until backend endpoint lands */}
      {endpointMissing && (
        <div className="stub-banner">
          <strong>TODO (Sprint 24.1 backend):</strong> Endpoint{' '}
          <code>GET /admin/audit-log</code> is not yet exposed via HTTP.
          Add it to the admin HTTP layer wrapping the append-only audit log store.
          Expected query params: <code>since</code>, <code>action</code>,{' '}
          <code>actor</code>, <code>limit</code>, <code>offset</code>.
        </div>
      )}

      {error && <div className="error-banner">{error}</div>}

      {/* Filter bar */}
      <form
        onSubmit={applyFilters}
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20, alignItems: 'flex-end' }}
      >
        <div className="form-group" style={{ marginBottom: 0, minWidth: 180 }}>
          <label htmlFor="al-action">Action</label>
          <select
            id="al-action"
            value={filterAction}
            onChange={e => setFilterAction(e.target.value)}
          >
            {KNOWN_ACTIONS.map(a => (
              <option key={a} value={a}>{a || '— all actions —'}</option>
            ))}
          </select>
        </div>

        <div className="form-group" style={{ marginBottom: 0, minWidth: 220 }}>
          <label htmlFor="al-actor">Actor (email or account_id)</label>
          <input
            id="al-actor"
            type="text"
            placeholder="system or email..."
            value={filterActor}
            onChange={e => setFilterActor(e.target.value)}
          />
        </div>

        <div className="form-group" style={{ marginBottom: 0, minWidth: 160 }}>
          <label htmlFor="al-since">Since</label>
          <input
            id="al-since"
            type="date"
            value={filterSince}
            onChange={e => setFilterSince(e.target.value ? new Date(e.target.value).toISOString() : '')}
          />
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading} style={{ alignSelf: 'flex-end' }}>
          {loading ? <span className="spinner" /> : null}
          Filter
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          style={{ alignSelf: 'flex-end' }}
          onClick={() => { setFilterAction(''); setFilterActor(''); setFilterSince(''); setOffset(0); }}
        >
          Clear
        </button>
      </form>

      {!loading && !endpointMissing && entries.length === 0 && !error && (
        <div className="empty-state">No audit log entries match the current filters.</div>
      )}

      {entries.length > 0 && (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>Changes</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, idx) => {
                  const rowKey = entry.log_id ?? String(idx);
                  const hasChanges = entry.changes_json != null &&
                    Object.keys(entry.changes_json ?? {}).length > 0;
                  return (
                    <>
                      <tr key={rowKey}>
                        <td className="text-muted" style={{ whiteSpace: 'nowrap' }}>
                          <span title={entry.timestamp}>{timeAgo(entry.timestamp)}</span>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                            {new Date(entry.timestamp).toLocaleString()}
                          </div>
                        </td>
                        <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {entry.actor_email
                            ? <span title={entry.actor_account_id}>{entry.actor_email}</span>
                            : entry.actor_account_id
                              ? <span className="text-mono" style={{ fontSize: 11 }}>{entry.actor_account_id.slice(0, 12)}…</span>
                              : <span className="text-muted">system</span>
                          }
                        </td>
                        <td>{actionBadge(entry.action)}</td>
                        <td>
                          {entry.target_type && (
                            <span className="badge badge-gray" style={{ marginRight: 4 }}>
                              {entry.target_type}
                            </span>
                          )}
                          {entry.target_id && (
                            <span className="text-mono" style={{ fontSize: 11 }}>
                              {entry.target_id.slice(0, 12)}{entry.target_id.length > 12 ? '…' : ''}
                            </span>
                          )}
                          {!entry.target_type && !entry.target_id && (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {hasChanges ? (
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => toggleExpand(rowKey)}
                            >
                              {expanded.has(rowKey) ? 'Hide ▲' : 'Show ▼'}
                            </button>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      </tr>
                      {expanded.has(rowKey) && hasChanges && (
                        <tr key={`${rowKey}-expanded`}>
                          <td colSpan={5} style={{ padding: 0 }}>
                            <pre className="raw-payload">
                              {JSON.stringify(entry.changes_json, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <span className="pagination-label" style={{ marginRight: 'auto' }}>
              {total} total entries
            </span>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setOffset(o => Math.max(0, o - LIMIT))}
              disabled={offset === 0 || loading}
            >
              Prev
            </button>
            <span className="pagination-label">Page {currentPage} / {pageCount}</span>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setOffset(o => o + LIMIT)}
              disabled={offset + LIMIT >= total || loading}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
